import { apiClient } from '@/hooks/useApi';

export interface DayHours {
  day: string;
  isOpen: boolean;
  open: string;
  close: string;
}

export interface HoursOverride {
  id: number;
  date: string;
  isClosed: boolean;
  openTime?: string;
  closeTime?: string;
  message?: string;
}

export interface CompanySettings {
  companyName: string;
  displayName?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  website?: string;
  logoBase64?: string;
  gstHstRegistrationNumber?: string;
  timezone?: string;
  currency?: string;
  weeklyHours?: string;
  smtpServer?: string;
  smtpPort?: number;
  smtpUsername?: string;
  smtpPassword?: string;
  enableSsl: boolean;
  emailFromAddress?: string;
  emailFromName?: string;
  smsEnabled: boolean;
  smsProvider?: string;
  smsAccountSid?: string;
  smsAuthToken?: string;
  smsFromPhoneNumber?: string;
  storageMode: 'CloudS3' | 'Local';
  notifyOnNewIntake: boolean;
  allowStatusChangeSms: boolean;
  allowStatusChangeEmail: boolean;
  allowIntakeConfirmationEmail: boolean;
  totalBays?: number;
  maxJobsPerTechnician?: number;
  seniorTechnicianJobLimit?: number;
  isTrial: boolean;
}

export const companyApi = {
  async getSettings(): Promise<CompanySettings> {
    const { data } = await apiClient.get<CompanySettings>('/api/company');
    return data;
  },

  async updateSettings(payload: Partial<CompanySettings>): Promise<CompanySettings> {
    const { data } = await apiClient.put<CompanySettings>('/api/company', payload);
    return data;
  },

  async updateNotificationPreferences(payload: {
    notifyOnNewIntake: boolean;
    allowStatusChangeSms: boolean;
    allowStatusChangeEmail: boolean;
    allowIntakeConfirmationEmail: boolean;
  }): Promise<CompanySettings> {
    const { data } = await apiClient.put<CompanySettings>('/api/company/notification-preferences', payload);
    return data;
  },

  async setStorageMode(mode: 'CloudS3' | 'Local'): Promise<void> {
    await apiClient.post('/api/company/storage-mode', { mode });
  },

  async getHours(): Promise<{ schedule: DayHours[]; overrides: HoursOverride[] }> {
    const { data } = await apiClient.get<{ schedule: DayHours[]; overrides: HoursOverride[] }>('/api/company/hours');
    return data;
  },

  async updateHours(schedule: DayHours[]): Promise<void> {
    await apiClient.put('/api/company/hours', schedule);
  },

  async uploadLogo(file: File): Promise<{ logoUrl: string }> {
    const form = new FormData();
    form.append('file', file);
    const { data } = await apiClient.post<{ logoUrl: string }>('/api/company/logo', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },
};
